<template>
  <div class="cart-products-table">
    <p v-if="useEditMode" class="body-semi-bold">
      {{ tStatic('markedRows') }}: {{ selectedRows.length }} / {{ selectableRowsCount }}
    </p>
    <oie-action-wrapper v-if="useEditMode">
      <template v-if="rows.length" #left>
        <slot name="customMultiActions"></slot>

        <oie-my-machines-selector-slide-in
          :disabled="selectedRows.length === 0 ? true : false"
          context="mark-multiple-rows"
          :tooltip="tStatic('markRowsWithMachineTooltip')"
          :text="tStatic('markRowsWithMachine')"
          @select-owner="$emit('markSelectedRows', $event)"
          @select-machine="$emit('markSelectedRows', $event)"></oie-my-machines-selector-slide-in>
        <oie-base-action
          theme="destructive"
          :disabled="selectedRows.length === 0 ? true : false"
          @click="$emit('deleteSelectedRows')">
          <template #iconPrepend>
            <oie-base-icon icon="trash-can" size="xs"></oie-base-icon>
          </template>
          {{ tStatic('cartClearSelectedArticles') }}
        </oie-base-action>
      </template>
      <template #right>
        <oie-file-upload-shopping-list-slide-in
          v-if="!hideFileUpload"
          accept=".csv, .txt"
          aria-describedby="file-upload-instructions"
          @add-file-to-list="$emit('add-file-to-list', $event)">
          <p id="file-upload-instructions" class="body">
            {{ tStatic('uploadQuickOrderFileDescription') }}
          </p>
          <a
            class="body-blue"
            :href="tStatic('quickOrderUploadInstructionsExampleUrl')"
            target="_blank"
            rel="noopener">
            {{ tStatic('quickOrderUploadInstructionsExample') }}
          </a>
        </oie-file-upload-shopping-list-slide-in>
      </template>
    </oie-action-wrapper>
    <div class="cart-products-table__table-container">
      <table
        :class="[
          'product-rows',
          'cart-products-table__table',
          {
            'product-rows--use-multiselect':
              mq('to-medium') &&
              useEditMode
          }
        ]">
        <thead>
          <tr>
            <th
              v-if="useEditMode && rows.length"
              class="product-row__head-remove"
              scope="col">
              <div class="product-row__head-inset">
                <oie-base-radio-checkbox
                  v-model="selectAll"
                  :label="tStatic('cartProductHeaderSelectAllLabel')"></oie-base-radio-checkbox>
              </div>
            </th>

            <th class="product-row__head-image" scope="col">
              <div class="visually-hidden">
                {{ tStatic("cartProductHeaderImageLabel") }}
              </div>
            </th>

            <th class="product-row__head-article-name" scope="col">
              <div class="product-row__head-inset">
                <span>
                  {{ tStatic("cartProductHeaderLabel") }}
                </span>
              </div>
            </th>

            <th class="product-row__head-stock-status" scope="col">
              <div class="product-row__head-inset">
                {{ tStatic("cartStatusSendNowLabel") }}
              </div>
            </th>

            <th class="product-row__head-stock-status" scope="col">
              <div class="product-row__head-inset">
                {{ tStatic("cartStatusSendLaterLabel") }}
              </div>
            </th>

            <th :class="{ 'product-row__head-quantity': useEditMode }" scope="col">
              <div class="product-row__head-inset">
                {{ tStatic("cartQuantityLabel") }}
              </div>
            </th>

            <th class="product-row__head-comment" scope="col">
              <div class="product-row__head-inset">
                {{ tStatic("cartRowCommentLabel") }}
              </div>
            </th>

            <th
              v-if="hasMyMachines"
              class="product-row__head-machine"
              scope="col">
              <div class="product-row__head-inset">
                {{ tStatic("cartRowMachineLabel") }}
              </div>
            </th>

            <th class="product-row__head-item-price" scope="col">
              <div
                class="product-row__head-inset product-row__head-inset--align-right">
                <span>{{ tStatic("cartRowPriceLabel") }}</span>
              </div>
            </th>

            <th class="product-row__head-total-price" scope="col">
              <div
                class="product-row__head-inset product-row__head-inset--align-right">
                <span>{{ tStatic("cartRowPriceTotalLabel") }}</span>
              </div>
            </th>

            <th
              v-if="useEditMode"
              class="product-row__head-remove"
              scope="col"></th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-if="rows && rows.length && mq('medium')"
            aria-hidden="true"
            class="cart-products__row-spacer">
            <td :colspan="getColumnSpans"></td>
          </tr>
          <template v-for="(row, i) of rows">
            <tr
              :key="`table-row-${rowIdentification(row)}`"
              :class="[
                {'cart-products__row-has-cross-sell--first-row': hasCartItemCrossSell(row)},
                {'product-row--disabled': isDisabledShoppingListItem(row)}
              ]"
              :data-unavailable-text="isDisabledShoppingListItem(row) ? tStatic('unavailableItemLabel') : null">
              <td v-if="useEditMode">
                <div class="product-row__body-inset product-row__body-inset--select">
                  <oie-base-radio-checkbox
                    v-if="!isDisabledShoppingListItem(row)"
                    v-model="selectedRowsProxy"
                    :value="rowIdentification(row)"
                    :aria-label="tStatic('selectCartItemLabel') + row.displayName"></oie-base-radio-checkbox>
                </div>
              </td>

              <td class="product-row__body-td--image">
                <div class="product-row__body-inset product-row__body-inset--article">
                  <oie-base-image
                    v-if="row.image || get(row, 'item.image')"
                    :images="`${getAssetPath(row.image) || getAssetPath(get(row, 'item.image'))}?preset=ProductThumb&watermark=OIEWatermarkSmall`"
                    :alt-text="row.displayName"
                    image-width="3.75rem"
                    image-height="3.75rem"
                    class="product-row__image"></oie-base-image>

                  <oie-base-image
                    v-else
                    :images="getAssetPath('/globalassets/missing404/404.png?preset=ProductThumb')"
                    :alt-text="row.displayName"
                    image-width="3.75rem"
                    image-height="3.75rem"
                    class="product-row__image"></oie-base-image>
                </div>
              </td>

              <td class="product-row__body-td--article">
                <div class="product-row__body-inset product-row__body-inset--article">
                  <!-- Product link - disabled for shopping list items -->
                  <a
                    v-if="useEditMode && (row.url || row.item.url) && !disableInternalItem(row) && !isDisabledShoppingListItem(row)"
                    :href="row.url || row.item.url"
                    class="product-row__name"
                    :data-pw="'item-link-' + row.itemId">
                    {{ row.displayName }}
                  </a>

                  <div v-else class="product-row__name">
                    {{ row.displayName }}
                  </div>

                  <div class="product-row__article-number">
                    {{ `${tStatic("headerCartItemNumberLabel")} ${row.itemId}` }}
                  </div>
                </div>
              </td>

              <td>
                <div v-if="mq('to-medium')" class="product-row__body-header">
                  {{ tStatic("cartStatusSendNowLabel") }}
                </div>

                <div v-if="!isQuoteItem(row)" class="product-row__body-inset">
                  <oie-product-status
                    v-if="row.stock <= 0"
                    status="in-stock"
                    text="0"
                    size="medium"></oie-product-status>

                  <oie-product-status
                    v-else-if="row.stock - row.quantity > 0"
                    status="in-stock"
                    :text="calculateStock(0, row.quantity)"
                    size="medium"></oie-product-status>

                  <oie-product-status
                    v-else-if="row.stock <= row.quantity"
                    status="in-stock"
                    :text="calculateStock(row.stock, 0)"
                    size="medium"></oie-product-status>
                </div>
              </td>

              <td>
                <div v-if="mq('to-medium')" class="product-row__body-header">
                  {{ tStatic("cartStatusSendLaterLabel") }}
                </div>

                <div v-if="!isQuoteItem(row)" class="product-row__body-inset">
                  <oie-product-status
                    v-if="row.stock < 0"
                    status="few-stock"
                    :text="`${row.quantity}`"
                    size="medium"></oie-product-status>

                  <oie-product-status
                    v-else
                    status="few-stock"
                    :text="calculateStock(row.stock, row.quantity)"
                    size="medium"></oie-product-status>
                </div>
              </td>

              <td>
                <div v-if="mq('to-medium')" class="product-row__body-header">
                  {{ tStatic("cartQuantityLabel") }}
                </div>

                <div class="product-row__body-inset">
                  <!-- Quantity input - disabled for shopping list items -->
                  <oie-product-quantity
                    v-if="useEditMode && !disableInternalItem(row) && !isDisabledShoppingListItem(row)"
                    :cart-row="row"
                    :stop-quantity-events="true"
                    @input="($event) => $emit('updateQuantity', {row: row.id, quantity: $event})">
                  </oie-product-quantity>

                  <!-- Show quantity as text for disabled items -->
                  <template v-else>
                    {{ row.quantity }}
                  </template>
                </div>
              </td>

              <td class="product-row__td--comment">
                <div v-if="mq('to-medium')" class="product-row__body-header">
                  {{ tStatic("cartRowCommentLabel") }}
                </div>

                <div class="product-row__body-inset">
                  <!-- Comments - disabled for shopping list items -->
                  <oie-comment
                    :title="tStatic('headerCartNotePlaceholder')"
                    :initial-value="row.comment"
                    :use-edit-mode="useEditMode && !isDisabledShoppingListItem(row)"
                    @save="(...args) => updateComment(row, ...args)"
                    @delete="(...args) => updateComment(row, '', ...args)">
                  </oie-comment>

                  <!-- ASSEMBLY NOTE -->
                  <oie-comment
                    v-if="isSuperUser && clientModel.cartType !== 0"
                    :title="tStatic('headerCartAssemblyNotePlaceholder')"
                    :initial-value="row.assemblyNote"
                    :use-edit-mode="useEditMode"
                    class="cart-assemblynote"
                    @save="(...args) => updateAssemblyNote(row, ...args)"
                    @delete="(...args) => updateAssemblyNote(row, '', ...args)"></oie-comment>
                </div>
              </td>

              <td v-if="hasMyMachines" class="product-row__td--machine">
                <div v-if="mq('to-medium')" class="product-row__body-header">
                  {{ tStatic("cartRowMachineLabel") }}
                </div>

                <div class="product-row__body-inset">
                  <div v-if="useEditMode" class="product-row__td--machine-actions">
                    <!-- Machine selector - disabled for shopping list items -->
                    <oie-my-machines-selector-slide-in
                      v-if="!isDisabledShoppingListItem(row)"
                      :context="row.id"
                      :tooltip="row.machineOwnerId || row.machineId ? tStatic('changeMachineTooltip'): ''"
                      :text="row.machineOwnerId || row.machineId ? tStatic('changeMachine') : tStatic('selectMachine')"
                      @select-owner="updateMachineOrOwner($event, row)"
                      @select-machine="updateMachineOrOwner($event, row)">
                    </oie-my-machines-selector-slide-in>
                    <oie-base-action
                      v-if="row.machineId || row.machineOwnerId"
                      v-tooltip.right="tStatic('clearMachineTooltip')"
                      class="product-row__td--machine-actions-cancel"
                      theme="destructive"
                      @click="updateMachineOrOwner({}, row)">
                      <template #iconPrepend>
                        <oie-base-icon icon="close" size="s"></oie-base-icon>
                      </template>
                    </oie-base-action>
                  </div>
                  <template v-if=" row.machineId || row.machineOwnerId">
                    <p v-if="row.machineOwnerId" class="product-row__td--machine-info">
                      <oie-base-icon icon="user-avatar" size="s"></oie-base-icon>{{ machineOwnerDictionary[row.machineOwnerId] }}
                    </p>
                    <p v-if="row.machineId" class="product-row__td--machine-info">
                      <oie-base-icon icon="machine" size="s"></oie-base-icon> {{ machineDictionary[row.machineId] }}
                    </p>
                  </template>
                  <p v-else class="product-row__td--machine-info">
                    {{ `(${tStatic('noMachineSelected')})` }}
                  </p>
                </div>
              </td>

              <td>
                <div v-if="mq('to-medium')" class="product-row__body-header">
                  {{ tStatic("cartRowPriceLabel") }}
                </div>

                <div
                  v-if="!isQuoteItem(row)"
                  class="product-row__body-inset product-row__body-inset--align-right product-row__body-inset--price"
                  :class="row.isDiscounted ? 'discounted' : ''">
                  <div v-if="!isRowInPriceEditMode(row.id)">
                    <div style="display: flex; gap: 5px;">
                      <span :data-pw="'price-' + row.itemId">{{ formatPrice(row.price) }}</span>
                      <button
                        v-if="isSuperUser && enablePriceEdit"
                        :aria-label="tStatic('editPrice') + ' - ' + row.displayName"
                        class="icon-button"
                        @click="togglePriceEdit(row.id)">
                        <oie-use-svg svg="pen"></oie-use-svg>
                      </button>
                    </div>
                  </div>
                  <template v-else>
                    <oie-cart-superuser-change-price
                      :cart-row="row"
                      @save-new-price="($event) => {
                        $emit('updatePrice', {row: row.id, price: $event})
                        togglePriceEdit(row.id)
                      }"
                      @close="togglePriceEdit($event)"></oie-cart-superuser-change-price>
                  </template>
                  <template v-if="isSuperUser && enablePriceEdit">
                    <p v-if="row.purchasePrice" class="product-row__body-purchase-price">
                      {{ tStatic("superUserPurchasePrice") }} {{ formatPrice(row.purchasePrice) }}
                    </p>
                    <p v-if="row.tb" class="product-row__body-purchase-price">
                      {{ tStatic("superUserCartRowTb") }} {{ formatPrice(row.tb) }}
                    </p>
                    <p v-if="row.tg" class="product-row__body-purchase-price">
                      {{ tStatic("superUserCartRowTg") }} {{ `${row.tg}%` }}
                    </p>
                  </template>
                </div>
              </td>

              <td>
                <div v-if="mq('to-medium')" class="product-row__body-header">
                  {{ tStatic("cartRowPriceTotalLabel") }}
                </div>

                <div
                  v-if="!isQuoteItem(row)"
                  class="product-row__body-inset product-row__body-inset--align-right product-row__body-inset--price">
                  {{ formatPrice(row.price * row.quantity) }}
                </div>
              </td>
              <td v-if="useEditMode">
                <div class="product-row__body-inset product-row__body-inset--remove">
                  <oie-base-button
                    css-classes="product-row__remove-button"
                    :aria-label="tStatic('removeCartItemLabel') + '-' + row.displayName"
                    @click="$emit('deleteRow', {row: row.id})"></oie-base-button>
                </div>
              </td>
            </tr>
            <tr :key="`return-fee-${rowIdentification(row)}`">
              <td colspan="11">
                <div v-if="row.returnFeeDescription" class="product-return-fee-description">
                  <span class="product-return-fee-icon"></span> {{ row.returnFeeDescription }}
                </div>
              </td>
            </tr>
            <tr v-if="isQuoteItem(row)" :key="`table-row-quote-info-${rowIdentification(row)}`" class="product-row--quote-info">
              <td :colspan="getColumnSpans">
                <div
                  v-html="tStatic('requestQuoteContent')"></div>
              </td>
            </tr>
            <!-- MANDATORY COMMENT -->
            <tr v-if="row.mandatoryComment && row.mandatoryComment.length" :key="`table-row-mandatory-comment-${rowIdentification(row)}`" class="product-row--mandatory-comment">
              <td :colspan="getColumnSpans">
                <div>
                  <div>
                    <span v-for="comment in row.mandatoryComment" :key="comment.key">
                      <span>{{ `${comment.title}: ` }}</span>{{ `${comment.value}` }}
                    </span>
                  </div>
                  <oie-base-action v-if="useEditMode" theme="skeleton" @click="editMandatoryComment(row)">
                    {{ tStatic('editMandatoryCommentLabel') }}
                    <template #iconAppend>
                      <oie-base-icon icon="edit" size="xs"></oie-base-icon>
                    </template>
                  </oie-base-action>
                </div>
              </td>
            </tr>
            <!-- CROSSELL -->
            <tr v-if="hasCartItemCrossSell(row)" :key="`table-row-crosssell-${rowIdentification(row)}`" class="cart-products__row-has-cross-sell--second-row">
              <td :colspan="getColumnSpans">
                <div style="width: 100%">
                  <oie-expandable-item
                    :heading="tStatic('cartCartItemCrossSellTitle')"
                    :open-heading="tStatic('cartCartItemCrossSellTitleOpen').replace('{0}', row.displayName)"
                    theme="secondary"
                    @toggle-accordion="handleToggleCrossSell($event)">
                    <oie-product-list-page-wrapper
                      ref="productList"
                      style="flex-grow: 1;"
                      :remember-scroll="false"
                      :show-matching-filter="false"
                      :use-compare="false"
                      :related-products="getCrossSellIds(row.crossSellProducts)"
                      :hide-show-more-link="true"
                      :show-filter="false"
                      :use-block="true"
                      :show-sort-order="false"
                      :page-size="getCrossSellIds(row.crossSellProducts).length"
                      :use-grid="mq('to-medium')"
                      :ignore-related="true"
                      :type-of-listing="listTypes.SPARE_PARTS_CROSS_SELL">
                    </oie-product-list-page-wrapper>
                  </oie-expandable-item>
                </div>
              </td>
            </tr>
            <!-- REPLACEMENT -->
            <tr v-if="hasCartItemReplacement(row)" :key="`table-row-replacement-${rowIdentification(row)}`" class="cart-products__row-has-replacement--second-row">
              <td :colspan="getColumnSpans">
                <div style="width: 100%">
                  <oie-expandable-item
                    :heading="tStatic('cartCartItemReplacementTitle')"
                    :open-heading="tStatic('cartCartItemReplacementTitleOpen').replace('{0}', row.displayName)"
                    theme="secondary"
                    @toggle-accordion="handleToggleReplacement($event)">
                    <oie-product-list-page-wrapper
                      ref="replacementList"
                      style="flex-grow: 1;"
                      :remember-scroll="false"
                      :show-matching-filter="false"
                      :use-compare="false"
                      :related-products="getReplacementIds(row.replacementProducts)"
                      :hide-show-more-link="true"
                      :show-filter="false"
                      :use-block="true"
                      :show-sort-order="false"
                      :page-size="getReplacementIds(row.replacementProducts).length"
                      :use-grid="mq('to-medium')"
                      :ignore-related="true"
                      :is-replacement="true"
                      :type-of-listing="listTypes.SPARE_PARTS_REPLACEMENT">
                    </oie-product-list-page-wrapper>
                  </oie-expandable-item>
                </div>
              </td>
            </tr>
            <!-- SPACER -->
            <tr
              v-if="rows && i < (rows.length - 1)"
              :key="`table-row-spacer-${rowIdentification(row)}`"
              aria-hidden="true"
              class="cart-products__row-spacer">
              <td :colspan="getColumnSpans"></td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import _ from 'lodash'
import { listTypes } from '@/core/analytics-variables'
import { showMandatoryCommentModal } from '@/core/modal-service'
import { productStatuses } from '@/variables/product-statuses'

export default {
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    useEditMode: {
      type: Boolean,
      default: true
    },
    enablePriceEdit: {
      type: Boolean,
      default: false
    },
    selectedRows: {
      type: Array,
      default: () => []
    },
    // temporary prop, we will implement file upload also in cart view in near future, then useEditMode will be only prop needed
    hideFileUpload: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    rowsInPriceEditMode: [],
    listTypes,
    selectAll: false
  }),
  computed: {
    ...mapGetters(['enableGtm']),
    ...mapGetters('myMachines', ['myMachines', 'machineOwnerDictionary', 'machineDictionary']),
    selectedRowsProxy: {
      get() {
        return this.selectedRows
      },
      set(value) {
        this.$emit('update:selectedRows', value)
      }
    },
    getColumnSpans: {
      get() { return this.myMachines ? '11' : '10' }
    },
    isSuperUser() {
      return (
        this.clientModel.impersonating !== null || this.clientModel.isSuperUser
      )
    },
    hasMyMachines() {
      return this.myMachines && (this.myMachines.machineOwners.length > 0 || this.myMachines.machines.length > 0)
    },
    selectableRowsCount() {
      return this.rows.filter(row => !this.isDisabledShoppingListItem(row)).length
    }
  },
  watch: {
    selectAll(newVal) {
      if(newVal) {
        // Only select non-disabled rows
        this.selectedRowsProxy = this.rows
          .filter(row => !this.isDisabledShoppingListItem(row))
          .map(row => row.id)
      } else {
        this.selectedRowsProxy = []
      }
    }
  },
  methods: {
    ...mapActions('myMachines', ['getMyMachines']),
    calculateStock(stock, quantity) {
      if (stock === 0) {
        return quantity.toString()
      }
      if (stock > quantity && quantity > 0) {
        return (0).toString()
      }
      if (quantity === 0) {
        return stock.toString()
      }

      return (quantity - stock).toString()
    },
    rowIdentification(row) {
      return row.id
    },
    async updateMachineOrOwner($event, row) {
      this.$emit('updateMachineOrOwner', { row: row.id, machineOrOwner: $event })
    },
    getCrossSellIds(items) {
      return _.map(items, item => item.id)
    },
    getReplacementIds(items) {
      return _.map(items, item => item.id)
    },
    hasCartItemCrossSell(row) {
      return row.crossSellProducts && row.crossSellProducts.length > 0
    },
    hasCartItemReplacement(row) {
      return row.replacementProducts && row.replacementProducts.length > 0
    },
    handleToggleCrossSell(isOpen) {
      window.dispatchEvent(new Event('resize'))
    },
    handleToggleReplacement(isOpen) {
      window.dispatchEvent(new Event('resize'))
    },
    async editMandatoryComment(row) {
      const updatedComments = await showMandatoryCommentModal({ mandatoryComment: { [row.itemId]: row.mandatoryComment }, title: this.tStatic('editMandatoryComment') })
      this.$emit('updateMandatoryComment', { row: row.id, mandatoryComment: updatedComments[row.itemId] })
    },
    updateComment(row, comment, callback) {
      this.$emit('updateComment', { row: row.id, comment, callback })
    },
    updateAssemblyNote(row, comment, callback) {
      this.$emit('updateAssemblyNote', { row: row.id, comment, callback })
    },
    togglePriceEdit(id) {
      this.rowsInPriceEditMode = _.xor(this.rowsInPriceEditMode, [id])
    },
    isRowInPriceEditMode(id) {
      return this.rowsInPriceEditMode.indexOf(id) >= 0
    },
    isQuoteItem(row) {
      // Check both publicationStatusCode and publicationStatus properties
      const statusCode = row.publicationStatusCode || row.publicationStatus
      return statusCode === productStatuses.noPrice
    },
    disableInternalItem(row) {
      const isInternalItem = row.publicationStatusCode === productStatuses.internal
      return isInternalItem && !this.isSuperUser
    },
    isDisabledShoppingListItem(row) {
      // Only apply disabled styling to shopping lists (cartType 0), not regular carts
      if (this.clientModel.cartType !== 0) {
        return false
      }

      // Check both publicationStatusCode and publicationStatus properties
      const statusCode = row.publicationStatusCode || row.publicationStatus

      // If statusCode is null/undefined, treat as disabled
      if (statusCode == null) {
        return true
      }

      // Check if the item has publication status 2 (discontinued) or 6 (expired)
      const hasDisabledStatus = statusCode === productStatuses.discontinued ||
                               statusCode === productStatuses.expired

      // Check if the item has no price or zero price
      const hasNoPrice = !row.price || row.price === 0 || row.price === '0'
      // console.log('row', row, 'hasNoPrice', hasNoPrice, 'statusCode', statusCode, 'hasDisabledStatus', hasDisabledStatus, 'productStatuses.noPrice', productStatuses.noPrice)
      return hasDisabledStatus || (hasNoPrice && statusCode !== productStatuses.noPrice) || !!row.isLongDelivery
    }
  }
}
</script>

<style lang="scss">
.visually-hidden {
  border: 0;
  clip-path: inset(50%);
  height: 1px;
  margin: 0;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}
.product-rows {
  display: block;
  width: 100%;

  @include media(medium) {
    display: table;
    border-collapse: collapse;
    border: 1px solid color(border-gray);
    background-color: color(background-white);
  }

  @media print {
    display: table;
    border-collapse: collapse;
    border: 1px solid color(border-gray);
    background-color: color(background-white);
  }
}

.grid--list .product-rows {
  @include media(medium) {
    border-top: 0;
  }
}

.product-rows thead {
  display: none;
  background-color: color(background-blue-6);

  @include media(medium) {
    display: table-header-group;
  }

  @media print {
    display: table-header-group;
  }
}

.product-rows--use-multiselect thead th:not(.product-row__head-remove) {
  display: none;
}

.product-rows--use-multiselect thead,
.product-rows--use-multiselect thead tr {
  display: block;
  background-color: transparent;
  padding-left: 1px;
}

.product-rows--use-multiselect thead th.product-row__head-remove {
  display: block;
  width: 100%;
  height: auto;

  > div {
    padding-left: 0;
  }
}

.product-rows tbody {
  @include media(to-medium) {
    display: block;
    width: 100%;
  }

  @media print {
    display: table-row-group;
    width: 100%;
  }
}

.product-rows tbody tr {
  @include media(to-medium) {
    display: block;
    width: 100%;
  }

  @media print {
    display: table-row;
    width: 100%;
  }
}

.product-rows tbody tr {
  @include media(to-medium) {
    border-right: 1px solid color(border-gray);
    border-left: 1px solid color(border-gray);
    background-color: color(background-white);
  }
}

.product-rows th,
.product-rows td {
  margin: 0;
  padding: 0;

  @media print {
    &:first-child {
      display: none;
    }
  }
}

.product-rows th {
  height: 3.375rem;
}

.product-rows td {
  display: flex;
  flex-direction: row;

  @include media(to-medium) {
    border-bottom: 1px solid color(border-gray);
  }

  @include media(medium) {
    display: table-cell;
    min-height: 6.25rem;
  }

  @media print {
    display: table-cell;
  }
}

.product-rows--order-history td {
  @include media(to-medium) {
    flex-direction: row;
  }
}

.product-rows--order-history td.product-row__td--comment {
  @include media(to-medium) {
    flex-direction: column;
  }
}

.product-rows tbody tr {
  border-top: 1px solid color(border-gray);
}

.product-rows.cart-products-table__table
  tr:hover td {
  background-color: initial !important;
}

.product-rows td.product-row__body-td--checkbox {
  border-bottom: 0;
}

.product-rows td.product-row__body-td--image {
  border-bottom: 0;
}

.product-rows td.product-row__td--comment {
  flex-direction: column;
}

/* table head */

.product-row__head-inset {
  @include body-smaller;
  position: relative;
  display: flex;
  align-items: flex-end;
  padding: measure(quarter);
  height: 100%;
}

.product-row__head-inset--align-right {
  justify-content: flex-start;

  @include media(medium) {
    justify-content: flex-end;
  }
}

.product-row__head-inset > span {
  pointer-events: none;
}

.product-row__head-inset:hover .table-sort {
  opacity: 1;
  visibility: visible;
}

.product-row__head-image {
  width: 6.25rem;
}

.product-row__head-article-name {
  width: 15rem;
}

.product-row__head-stock-status {
  width: 12.5rem;
}

.product-row__head-quantity {
  width: 8.125rem;
}

.product-row__head-comment {
  min-width: 16.875rem;
}

.product-row__head-item-price {
  width: 10.625rem;
}

.product-row__head-total-price {
  width: 8.4375rem;
}

.product-row__head-remove {
  width: 2.5rem;

  @media print {
    display: none;
  }
}

// .product-rows thead tr th {
//   width: auto;
// }

.product-row__head-order-number {
  width: 14.0625rem;
}

.product-row__head-order-date {
  width: 12.25rem;
}

.product-row__head-product-amount {
  width: 10rem;
}

.product-row__head-total-sum {
  width: 10.4375rem;
}

/* table body */

.product-row__body-inset {
  @include body-smaller;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  margin-left: auto;
  padding: measure(quarter);
  height: 100%;

  @include media(medium) {
    min-height: 6.25rem;
  }
}

.product-rows--order-history .product-row__body-inset {
  @include media(to-medium) {
    margin-left: auto;
  }
}

.product-row__body-td--article {
  @media print {
    width: 100%;
  }
}

.product-row__body-td--article .product-row__body-inset {
  margin-left: 0;
}

.product-row__body-td--checkbox .product-row__body-inset {
  margin-left: 0;
}

.product-row__td--comment .product-row__body-inset {
  margin-left: 0;
}

.product-row__body-td--image .product-row__body-inset {
  margin-left: 0;
}

.product-row__body-inset--align-right {
  align-items: flex-end;
}

.product-row__body-inset--price {
  @include body-smaller;

  @include media(medium) {
    @include body-semi-bold;
  }
}

.product-row__body-inset--bold {
  @include body-semi-bold;
}

.product-row__body-inset--select {
  justify-content: center;
  margin: 0;
}

.product-row__body-inset--article {
  position: relative;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
}

.product-row__body-inset--remove {
  @include media(to-medium) {
    align-items: center;
  }
}

.product-row__body-header {
  @include body-smaller-semi-bold;
  padding: measure(quarter);

  @include media(medium) {
    display: none;
  }

  @media print {
    display: none;
  }
}

.product-row__body-purchase-price {
  margin-top: measure(quarter);
  font-size: 13px;
  font-weight: 400;
  white-space: nowrap;
}

.product-row__name {
  @include body-smaller-semi-bold;
  color: color(text-blue);
  text-decoration: none;
  margin: 0 0 measure(eighth) 0;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }

  @include media(medium) {
    @include body-semi-bold;
    color: color(text);
  }
}

.product-rows--readonly .product-row__name:hover {
  text-decoration: none;
}

.product-row__article-number {
  @include body-smaller;
}

.product-row__order-number {
  @include body-semi-bold;
  color: color(text-blue);
  margin: 0;
  padding: 0;
}

.product-tabs__variants .product-rows tbody tr {
  @include media(to-medium) {
    border-right: none;
    border-left: none;
  }
}

.product-row__image {
  height: 3.75rem;
  width: 3.75rem;
}

.product-row__remove-button {
  @include reset-button;
  @include background-svg(trashbin);
  width: 1.0625rem;
  height: 1.25rem;
  padding: 0;
}

.product-row__remove-button > svg {
  width: 100%;
  height: 100%;
}

.product-row__comment-textarea {
  flex-grow: 1;
  width: 100%;
  padding: measure(quarter);
  background-color: #fafafa;
  border: none;
  box-shadow: 0 0 4px 0 rgba(170, 170, 170, 0.5);
  resize: none;
}

.product-row__comment-edit-done {
  background-color: transparent;
  border: none;
  margin: 0;
  padding: 0;
  position: absolute;
  top: measure(half);
  right: measure(half);
  width: 1.25rem;
  height: 1.25rem;
}

.product-row__comment-edit-done > svg {
  width: 100%;
  height: 100%;
}

.cart-assemblynote {
  margin-top: measure(quarter);
}

.discounted {
  color: red;
}

// Cross-sell styles BEGIN

.product-rows tbody tr.cart-products__row-has-cross-sell--first-row {
  @include media(to-medium) {
    margin-bottom: 0;
  }
}

.cart-products__row-spacer {
  border-left: 1px solid #F6F6F6 !important;
  border-right: 1px solid #F6F6F6 !important;
}

.cart-products__row-spacer td{
  background-color: #F6F6F6;
  border-left: 1px solid #F6F6F6 !important;
  border-right: 1px solid #F6F6F6 !important;
  border-top: none !important;
  border-bottom: none !important;
  height: measure(quarter);
}

// Cross-sell styles END

// My machine related styles
.product-row__td--machine-actions {
  display: flex;
  gap: 0.25rem;
}

.product-row__td--machine-actions-cancel {
  padding: 3px;

  > svg:first-child:not(:only-child) {
    margin-right: 0;
  }
}

.product-row__td--machine-info {
  display: flex;
  align-items: center;
  padding: measure(eighth) measure(quarter) measure(eighth) measure(quarter);
}

.product-row__td--machine-info>:first-child {
  margin-right: measure(eighth);
}

// Mandatory comment styles BEGIN
.product-row--mandatory-comment,
.product-row--quote-info {
  position: relative;
  background-color: color(background-yellow) !important;
  border: 1px solid color(border-yellow) !important;
}

.product-row--mandatory-comment td,
.product-row--quote-info td {
  @include body;
  padding: measure(quarter);
}

.product-row--mandatory-comment td > div {
  display: flex;
  align-items: center;
  gap: measure(quarter);
}

.product-row--mandatory-comment td div > span > span {
  @include body-semi-bold;
}

.product-row--mandatory-comment .base-action {
  position: static;
  text-decoration: underline;
}

.product-row--mandatory-comment .base-action::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}
// Mandatory comment styles END

.cart-products-table >*:not(:last-child) {
  margin-bottom: measure(quarter);
}

.cart-products-table__table-container {
  overflow: auto;
  max-height: 70vh;
}

// Disabled shopping list item styles - Enhanced visibility without strikethrough
.product-row--disabled {
  background-color: #f0f0f0 !important;
  position: relative;
  border-left: 4px solid #d32f2f !important;
}

.product-row--disabled td {
  background-color: #f0f0f0 !important;
  color: #757575 !important;
}

// Product name styling for disabled items - no strikethrough, clearer disabled appearance
.product-row--disabled .product-row__name {
  color: #757575 !important;
  pointer-events: none;
  font-style: italic;
  opacity: 0.8;
}

.product-row--disabled .product-row__article-number {
  color: #757575 !important;
  font-style: italic;
  opacity: 0.8;
}

// Price styling for disabled items - no strikethrough
.product-row--disabled .product-row__body-inset--price {
  color: #757575 !important;
  opacity: 0.8;
}

// Images and status indicators - more pronounced graying
.product-row--disabled .product-row__image {
  filter: grayscale(100%) opacity(0.5);
}

.product-row--disabled .oie-product-status {
  filter: grayscale(100%) opacity(0.5);
}

// Disable form controls and interactive elements
.product-row--disabled .oie-product-quantity,
.product-row--disabled .oie-comment,
.product-row--disabled .oie-my-machines-selector-slide-in,
.product-row--disabled .oie-base-action:not(.product-row__remove-button),
.product-row--disabled .icon-button:not(.product-row__remove-button) {
  pointer-events: none;
  opacity: 0.3;
  filter: grayscale(100%);
}

// Enhanced "UNAVAILABLE" indicator with better visibility - positioned on the right
.product-row--disabled::after {
  content: attr(data-unavailable-text);
  position: absolute;
  top: 3%;
  right: 1%;
  @include body-smaller;
  font-weight: 700;
  color: #ffffff;
  background-color: #d32f2f;
  padding: 3px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 1;
  font-size: 10px;
}

// Keep checkboxes and delete buttons functional for disabled rows
.product-row--disabled .oie-base-checkbox,
.product-row--disabled .oie-base-checkbox input[type="checkbox"],
.product-row--disabled .product-row__remove-button {
  pointer-events: auto;
  opacity: 1;
  filter: none;
  position: relative;
  z-index: 2;
}

// Ensure delete button container allows interactions
.product-row--disabled .product-row__body-inset--remove {
  pointer-events: auto;
}

// Disable product links - no strikethrough
.product-row--disabled a {
  pointer-events: none;
  color: #757575 !important;
  text-decoration: none !important;
}

.product-return-fee-description {
  border: solid #E9A638 1px;
  padding: 0.4rem 0.4rem 0.4rem 0.8rem;
  display: flex;
}

.product-return-fee-icon {
  flex-shrink: 0;
  margin-right: measure(eighth);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  color: color(background-warning);
  background-image: url("data:image/svg+xml,%3Csvg width='50' height='50' viewBox='0 0 50 50' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M47.654 30.041c.36-1.623.552-3.31.552-5.041 0-1.797-.205-3.546-.592-5.226l2.336-1.359a25.856 25.856 0 0 0-2.755-6.576l-2.618.715a23.292 23.292 0 0 0-7.287-7.207l.699-2.643A25.416 25.416 0 0 0 31.394 0L30.03 2.386a23.282 23.282 0 0 0-5.004-.543c-1.796 0-3.545.204-5.224.59L18.415.049A26.17 26.17 0 0 0 11.842 2.8l.729 2.668a23.288 23.288 0 0 0-7.2 7.256l-2.67-.706A26.103 26.103 0 0 0 0 18.613l2.396 1.368A23.202 23.202 0 0 0 1.848 25c0 1.791.204 3.535.589 5.209L.053 31.595a26.114 26.114 0 0 0 2.756 6.573l2.664-.728a23.29 23.29 0 0 0 7.26 7.194l-.705 2.668A25.382 25.382 0 0 0 18.625 50l1.366-2.392c1.622.358 3.307.55 5.036.55 1.784 0 3.521-.203 5.19-.584l1.38 2.37a25.917 25.917 0 0 0 6.574-2.756l-.72-2.636a23.291 23.291 0 0 0 7.226-7.266l2.621.693A25.444 25.444 0 0 0 50 31.38l-2.346-1.339z' fill='%23E9A638'/%3E%3Cpath fill='%23FFF' d='M23.154 34.65L15 26.856l2.438-2.53 5.373 5.136L34.421 15l2.75 2.19z'/%3E%3C/g%3E%3C/svg%3E");
  width: 1.375rem;
  height: 1.375rem;
}
</style>
